import clamp from "lodash/clamp";
import isNil from "lodash/isNil";
import isNumber from "lodash/isNumber";
import isUndefined from "lodash/isUndefined";
import { roundToDecimals } from "PFCore/helpers/rounding";
import { useFirstRender } from "PFCore/hooks";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { useCallback, useEffect, useMemo, useState } from "react";

const MAX_DECIMALS = 2;

export type UseNumberInput = {
  onChange: (value: number | undefined) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
} & (
  | { defaultValue: number | undefined; controlledValue?: never }
  | { defaultValue?: never; controlledValue: number | undefined }
);

interface UseNumberInputReturn {
  value: string;
  handleChange: (value: string) => void;
}

const getLimitedValue = (value: number, min: number, max: number): number =>
  roundToDecimals(clamp(value, min, max), MAX_DECIMALS);

const getLocaleDecimalSeparator = (locale?: string) => {
  const formattedNumber = new Intl.NumberFormat(locale ?? "en").format(1.1);
  return formattedNumber.replace(/\d/g, "")[0];
};

export const useNumberInput = ({
  defaultValue,
  controlledValue,
  onChange,
  min = 0,
  max = Infinity,
  disabled
}: UseNumberInput): UseNumberInputReturn => {
  const { data: currentProfile } = useCurrentProfile();
  const isFirstLoad = useFirstRender();

  const { decimalPoint, numberWithDecimalsRegex } = useMemo(() => {
    const decimalPoint = getLocaleDecimalSeparator(currentProfile.locale);
    const decimalPointEscaped = decimalPoint.replace(".", "\\.");
    const numberWithDecimalsRegex = new RegExp(`^\\d+(${decimalPointEscaped}\\d{0,${MAX_DECIMALS}})?$`);
    return { decimalPoint, numberWithDecimalsRegex };
  }, [currentProfile.locale]);

  const [value, setValue] = useState<string>(() => {
    if (isNumber(defaultValue ?? controlledValue)) {
      return String(defaultValue ?? controlledValue).replaceAll(".", decimalPoint);
    }
    return "";
  });

  const handleChange = useCallback(
    (inputValue: string): void => {
      if (disabled) {
        return;
      }
      if (!inputValue) {
        setValue("");
        onChange(undefined);
        return;
      }
      if (!numberWithDecimalsRegex.test(inputValue)) {
        return;
      }
      if (!inputValue?.endsWith(decimalPoint)) {
        const numericValue = Number(inputValue.replaceAll(decimalPoint, "."));
        const newValue = getLimitedValue(numericValue, min, max);
        // Keep the raw typed string when clamping/rounding didn't change the value,
        // so a trailing/mid decimal "0" (e.g. "9.0") isn't collapsed to "9" and its dot lost
        const newDisplayValue =
          newValue === numericValue ? inputValue : String(newValue).replaceAll(".", decimalPoint);
        setValue(newDisplayValue);
        onChange(newValue);
        return;
      }
      const [integerPart] = inputValue.split(decimalPoint);
      const newIntegerValue = getLimitedValue(Number(integerPart), min, max);
      setValue(`${newIntegerValue}${decimalPoint}`);
      onChange(newIntegerValue);
    },
    [min, max, onChange, decimalPoint, numberWithDecimalsRegex, disabled]
  );

  useEffect(() => {
    if (isNil(value) || isFirstLoad || disabled) {
      return;
    }
    handleChange(value);
  }, [max]);

  useEffect(() => {
    if (isNil(controlledValue)) {
      if (isUndefined(defaultValue) && value !== "") {
        setValue("");
      }

      return;
    }
    const newStringValue = String(controlledValue).replaceAll(".", decimalPoint);
    if (newStringValue !== value) {
      handleChange(newStringValue);
    }
  }, [controlledValue]);

  return {
    value,
    handleChange
  };
};
